import { PropsWithChildren, useCallback, useEffect } from 'react';

import { useBlocker } from 'react-router-dom';

import {
    Channel,
    EventType,
    registerNativeBackPressed,
    unregisterNativeBackPressed,
    useBackKeyInterceptorStore,
    useChannelContext,
    useCurrentPage,
    useEventBusEmit,
    useRouter,
} from 'scbk-services';
import { useGlobalComponentsStore, usePopupStore } from 'scbk-shared-module';

/**
 * 브라우저/네이티브 백버튼 처리
 * @returns
 */
const BackKeyProvider: React.FC<PropsWithChildren> = () => {
    const { channel } = useChannelContext();
    const { currentPageId } = useCurrentPage();
    const router = useRouter();
    const emit = useEventBusEmit();
    const blocker = useBlocker(({ historyAction }) => {
        return historyAction === 'POP';
    });

    /**
     * 백키 인터셉터 함수를 실행 하고.. 결과값을 반환한다.
     */
    const dispatchBackKeyHandler = useCallback((type: 'page' | 'popup') => {
        const backKeyHandler = useBackKeyInterceptorStore.getState().get(type);
        if (backKeyHandler) {
            const result = backKeyHandler();
            return result;
        }
        return true;
    }, []);

    /**
     * 팝업 노출된 정보 length를 가져온다.
     */
    const getPopupSize = useCallback(() => {
        const popupStore = usePopupStore.getState();
        const popupSize = Object.values(popupStore.entry).filter(({ visible }) => visible).length;
        return popupSize;
    }, []);

    /**
     * 다이얼로그 length를 가져온다.
     */
    const getPendingActionDialogsLength = useCallback(() => {
        const pendingActionDialogs = useGlobalComponentsStore.getState().pendingActionDialogs;
        return pendingActionDialogs.length;
    }, []);

    /**
     * 뒤로가기 처리
     */
    const processBack = useCallback(() => {
        //**************************
        //화면에 노출된 다이얼로그 존재여부 체크
        //**************************
        const pendingActionDialogsLength = getPendingActionDialogsLength();
        if (pendingActionDialogsLength > 0) {
            return;
        }

        //**************************
        // 팝업/바텀시트 백키 인터셉터 처리
        //**************************
        const handle = dispatchBackKeyHandler('popup');
        if (!handle) {
            return false;
        }

        //**************************
        // 팝업/모달/바텀시트 처리
        // 팝업/모달 등 전역으로 관리되는 팝업이 있다면.. 먼저 닫는다.
        //**************************
        const popupStore = usePopupStore.getState();
        const popupSize = getPopupSize();
        if (popupSize > 0) {
            const last = popupStore.peek(true); //활성화된 팝업 정보만 가져온다.
            if (!last) {
                return false;
            }

            const id = last.id;
            popupStore.clear(id);
            return false;
        }

        //**************************
        // 화면 뒤로가기 처리
        //**************************
        //router.back();
        const backBtn = document.getElementById('btnHeaderBack');
        if (backBtn && backBtn instanceof HTMLElement && backBtn.offsetParent != null) {
            backBtn.click();
            return;
        }

        //로그인 수단 화면인 경우
        if (currentPageId === 'LGNMTH000_00MS') {
            router.skipBack();
            return;
        }

        //**************************
        //로그아웃팝업 호출
        //**************************
        emit(EventType.OpenLogoutPopup);
    }, [router, dispatchBackKeyHandler, getPopupSize, currentPageId]);

    useEffect(() => {
        if (channel === Channel.MB) {
            return;
        }

        if (blocker.state === 'blocked') {
            //processBack();
            blocker.reset();
        }
    }, [channel, blocker]);

    useEffect(() => {
        //개발모드가 아닌경우 return;
        if (import.meta.env.MODE !== 'development') {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                processBack();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [processBack]);

    //-------------------------------------------
    //네이티브 안드로이드 웹뷰 onBackPressed 처리
    //-------------------------------------------
    useEffect(() => {
        if (channel !== Channel.MB) {
            return;
        }

        registerNativeBackPressed(() => {
            processBack();
        });

        return () => {
            unregisterNativeBackPressed();
        };
    }, [channel, processBack]);

    //-------------------------------------------
    // PC뱅킹 브라우저 뒤로가기 막기 처리
    //-------------------------------------------
    // useEffect(() => {
    //     if (channel === Channel.MB) {
    //         return;
    //     }

    //     const blockHistory = () => {
    //         window.history.pushState(null, '', window.location.href);
    //     };

    //     blockHistory();

    //     const handlePopState = () => {
    //         blockHistory();
    //     };

    //     window.addEventListener('popstate', handlePopState);
    //     return () => {
    //         window.removeEventListener('popstate', handlePopState);
    //     };
    // }, [channel]);

    return null;
};

export { BackKeyProvider };
